/** Zustand persist store — admin authentication (独立于用户认证)
 *
 * Token model (v6):
 *   - accessToken + refreshToken + sessionId: ĐỀU persisted localStorage.
 *     LÝ DO: trước đây accessToken memory-only → MỌI F5 mất token → request
 *     đầu 401 → buộc phải refresh. Refresh ROTATE token; nếu 2 tab (hoặc đua
 *     request) lỡ gửi token đã xoay → BE coi là reuse và REVOKE cả session →
 *     "logout hoài". Persist accessToken ⇒ reload KHÔNG cần refresh (token còn
 *     hạn 1h) ⇒ giảm hẳn số lần refresh ⇒ hết đua. Refresh giờ chỉ chạy khi
 *     token thật sự hết hạn (admin-api lo cross-tab lock + sync).
 *     Tradeoff XSS không đổi: refresh token (nhạy hơn) vốn đã ở localStorage.
 *     Nâng cấp httpOnly cookie là việc của BE sau này.
 *
 * v3: AdminUser thay bằng type từ types/admin-rbac.ts (role/team_id/...).
 *     Migrate v2 → v3: gán role mặc định "super_admin" cho user cũ.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AdminUser, Role } from "@/types/admin-rbac";

export type { AdminUser };

interface AdminAuthState {
  // State
  isAuthenticated: boolean;
  admin: AdminUser | null;
  accessToken: string | null;
  refreshToken: string | null; // persisted (v5) — fuel cho on401 refresh
  /** UUID session — cần để call /auth/refresh (BE expect {session_id, refresh_token}). */
  sessionId: string | null;

  // Actions
  setAuth: (
    admin: AdminUser,
    accessToken: string,
    refreshToken: string,
    sessionId: string,
  ) => void;
  clearAuth: () => void;
  setTokens: (accessToken: string, refreshToken: string, sessionId?: string) => void;
  /** Cập nhật một phần admin object (vd avatar_url sau upload). Không đụng token. */
  updateAdmin: (partial: Partial<AdminUser>) => void;
  // Convenience selectors
  role: () => Role | null;
  teamId: () => string | null;
}

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      admin: null,
      accessToken: null,
      refreshToken: null,
      sessionId: null,

      setAuth: (admin, accessToken, refreshToken, sessionId) =>
        set({
          isAuthenticated: true,
          admin,
          accessToken,
          refreshToken,
          sessionId,
        }),

      clearAuth: () =>
        set({
          isAuthenticated: false,
          admin: null,
          accessToken: null,
          refreshToken: null,
          sessionId: null,
        }),

      setTokens: (accessToken, refreshToken, sessionId) =>
        set((s) => ({
          accessToken,
          refreshToken,
          sessionId: sessionId ?? s.sessionId,
        })),

      updateAdmin: (partial) =>
        set((s) => ({
          admin: s.admin ? { ...s.admin, ...partial } : null,
        })),

      role: () => get().admin?.role ?? null,
      teamId: () => get().admin?.team_id ?? null,
    }),
    {
      name: "xfincompass-admin-auth", // 独立 localStorage key
      version: 6,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        admin: state.admin,
        accessToken: state.accessToken, // v6: persisted (xem comment đầu file)
        refreshToken: state.refreshToken,
        sessionId: state.sessionId,
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      migrate: (persisted: any, version: number) => {
        if (version < 2) {
          // v2: remove tokens from persisted state (now memory + httpOnly cookie)
          delete persisted.accessToken;
          delete persisted.refreshToken;
        }
        if (version < 5) {
          // v5: refreshToken+sessionId giờ ĐƯỢC persist (fuel cho on401
          // refresh). Bản cũ không lưu → phiên hiện tại không có fuel,
          // lần 401 đầu sẽ về /login một lần duy nhất rồi ổn từ đó.
          persisted.refreshToken = persisted.refreshToken ?? null;
          persisted.sessionId = persisted.sessionId ?? null;
        }
        if (version < 4 && persisted?.admin) {
          // v3+v4: ensure RBAC fields exist. v4 cũng vá cho session v3 cũ
          // đã được save trước khi mock thêm field `role` — nếu thiếu thì default
          // "super_admin" (dev/mock) để sidebar không bị ẩn hết tab.
          persisted.admin = {
            id: persisted.admin.id,
            email: persisted.admin.email,
            display_name: persisted.admin.display_name,
            role: persisted.admin.role ?? "super_admin",
            team_id: persisted.admin.team_id ?? null,
            manager_id: persisted.admin.manager_id ?? null,
            is_active: persisted.admin.is_active ?? true,
            is_online: persisted.admin.is_online,
            avatar_url: persisted.admin.avatar_url,
            advisor_profile: persisted.admin.advisor_profile,
          };
        }
        return persisted;
      },
    },
  ),
);
